import React, { useCallback, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { CurrencyInput } from 'ui-components/components/currency-input';
import { DropdownMenu } from 'ui-components/components/dropdown-menu/';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { PercentageInput } from 'ui-components/components/percentage-input';
import { emptyObject } from 'ui-components/default-props/empty';

import Global from '../../../../../../store/global-selectors';
import messages from '../../messages';
import { SET_AMOUNT, PERCENTAGE_OF_CARRIER_RATE } from '../constants';
import { DeliveryOptionsContext } from '../context';
import DeliveryOptionsItemPropType from '../delivery-option-item-prop-type';
import { addedFeesModes } from '../enums';
import style from './edit-delivery-option-page.module.less';

const getAdditionalFeeMode = (additionalFeesObject) => {
  return parseInt(additionalFeesObject?.percentage, 10) !== 0 ? PERCENTAGE_OF_CARRIER_RATE : SET_AMOUNT;
};

const AdjustmentConfiguration = ({ additionalFees = emptyObject, item, setItem }) => {
  const [selectedAdjustmentMethod, setSelectedAdjustmentMethod] = useState(getAdditionalFeeMode(item.additionalFees));
  const sellerCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const onChangePercentVsSetAmountDropdownCallback = useCallback(
    (value) => {
      setSelectedAdjustmentMethod(value);
      setItem({
        ...item,
        additionalFees: {
          ...item.additionalFees,
          percentage: 0,
          flatRate: {
            value: 0,
          },
        },
      });
    },
    [item, setItem],
  );
  const onSetAdditionalFeesFlatRateCallback = useCallback(
    (value) => {
      setItem({
        ...item,
        additionalFees: {
          ...item.additionalFees,
          flatRate: {
            ...item.additionalFees.flatRate,
            value: parseFloat(value),
            amount: parseFloat(value),
            code: sellerCurrencyCode,
          },
        },
      });
    },
    [item, sellerCurrencyCode, setItem],
  );
  const onSetAdditionalFeesPercentageCallback = useCallback(
    (percentage) => {
      setItem({
        ...item,
        additionalFees: {
          ...item.additionalFees,
          percentage,
        },
      });
    },
    [item, setItem],
  );
  const { contextMessages } = useContext(DeliveryOptionsContext);

  return (
    <div className={style['additional-fees-controls']}>
      <div className={style['additional-fee-row']}>
        <div className={style['live-rate-title']}>
          <FormattedMessage
            {...messages.adjustBy}
            values={{
              additionalFeesMode: additionalFees.mode,
            }}
          />
        </div>
        <div>
          <DropdownMenu className={style['section-control-dropdown-menu']} dropdownMenuShouldHaveWidthOfButton>
            <DropdownMenuToggler className={style['rate-adjustment-dropdown-button']} theme='white'>
              {selectedAdjustmentMethod === SET_AMOUNT ? (
                <FormattedMessage {...messages.setAmountValue} />
              ) : (
                <FormattedMessage {...messages.percentageOfCarrierRateValue} />
              )}
            </DropdownMenuToggler>
            {addedFeesModes.map((mode) => (
              <DropdownMenuItem key={mode.id} name={mode.id} onClick={onChangePercentVsSetAmountDropdownCallback}>
                {mode.displayName}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
        </div>
      </div>
      {selectedAdjustmentMethod === PERCENTAGE_OF_CARRIER_RATE && (
        <div className={style['additional-fee-row']}>
          <div className={style['live-rate-title']}>
            <FormattedMessage {...messages.percentageOfCarrierRate} />
          </div>
          <div className={style['additional-fees-input-container']}>
            <PercentageInput
              className={style['percentage-input']}
              onChange={onSetAdditionalFeesPercentageCallback}
              value={additionalFees.percentage}
            />
            <div>
              <FormattedMessage
                {...contextMessages.adjustPercentageDescription}
                values={{
                  additionalFeesMode: additionalFees.mode,
                }}
              />
            </div>
          </div>
        </div>
      )}
      {selectedAdjustmentMethod === SET_AMOUNT && (
        <div className={style['additional-fee-row']}>
          <div className={style['live-rate-title']}>
            <FormattedMessage {...messages.setAmount} />
          </div>
          <div className={style['additional-fees-input-container']}>
            <CurrencyInput
              className={style['currency-input']}
              currencyCode={sellerCurrencyCode}
              onChange={onSetAdditionalFeesFlatRateCallback}
              onlyPositive
              value={additionalFees.flatRate?.value}
            />
            <div>
              <FormattedMessage
                {...contextMessages.adjustSetAmountDescription}
                values={{
                  additionalFeesMode: additionalFees.mode,
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdjustmentConfiguration;

AdjustmentConfiguration.propTypes = {
  additionalFees: PropTypes.shape({
    flatRate: PropTypes.shape({
      value: PropTypes.number,
    }),
    mode: PropTypes.string,
    percentage: PropTypes.number,
  }),
  item: DeliveryOptionsItemPropType.isRequired,
  setItem: PropTypes.func.isRequired,
};
